import type { LatestReasoningResponse } from "@/types/analysis";

function toWsBaseUrl(apiBaseUrl: string): string {
  const trimmed = (apiBaseUrl || "").trim().replace(/\/$/, "");
  if (!trimmed) return "";
  // http:// -> ws://, https:// -> wss://
  return trimmed.replace(/^http/i, (m) => (m.toLowerCase() === "https" ? "wss" : "ws"));
}

export function buildAnalysisWsUrl(params: {
  apiBaseUrl: string;
  requestId: number;
  accessToken: string;
}): string {
  const wsBase = toWsBaseUrl(params.apiBaseUrl);
  const token = encodeURIComponent(params.accessToken);
  return `${wsBase}/ws/analysis/${params.requestId}?token=${token}`;
}

export type NormalizedWsEvent = {
  eventType: "STATUS" | "RESULT_READY" | "ERROR" | "UNKNOWN";
  payload: any;
  requestId?: number;
};

export function normalizeAnalysisWsMessage(raw: unknown): NormalizedWsEvent | null {
  if (raw == null) return null;

  let msg: any = raw;
  if (typeof raw === "string") {
    try {
      msg = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!msg || typeof msg !== "object") return null;

  const tRaw = msg.event ?? msg.type ?? msg.event_type ?? msg.kind ?? msg.name;
  const t = typeof tRaw === "string" ? tRaw.toUpperCase() : "";
  const payload = msg.payload ?? msg.data ?? msg;

  const requestIdRaw = payload?.request_id ?? payload?.requestId ?? msg?.request_id ?? msg?.requestId;
  const requestId = typeof requestIdRaw === "number" ? requestIdRaw : undefined;

  if (t === "STATUS" || t === "RESULT_READY" || t === "ERROR") {
    return { eventType: t, payload, requestId };
  }

  // Some servers may send {STATUS:{...}} shapes; try to detect
  if (msg.STATUS) return { eventType: "STATUS", payload: msg.STATUS, requestId };
  if (msg.RESULT_READY) return { eventType: "RESULT_READY", payload: msg.RESULT_READY, requestId };
  if (msg.ERROR) return { eventType: "ERROR", payload: msg.ERROR, requestId };

  return { eventType: "UNKNOWN", payload, requestId };
}

export function statusPayloadToLatestReasoning(params: {
  existing?: LatestReasoningResponse;
  payload: any;
  symbolHint?: string;
}): LatestReasoningResponse {
  const p = params.payload || {};
  const prevPhase = Number(params.existing?.phase_number ?? 1);

  const phase = Number(
    p.phase_number ?? p.phase ?? p.phaseNum ?? p.phaseIndex ?? params.existing?.phase_number ?? 1
  );

  const phaseChanged = Number.isFinite(phase) && Number.isFinite(prevPhase) && phase > prevPhase;
  const hasFreshReason = p.live_reasoning != null || p.reason != null || p.reasoning != null;
  const hasFreshSummary = p.market_health_summary != null || p.marketHealthSummary != null;

  const reasonRaw = hasFreshReason
    ? (p.live_reasoning ?? p.reason ?? p.reasoning)
    : phaseChanged
      ? ""
      : (params.existing?.reason ?? "");
  const reason = Array.isArray(reasonRaw) ? reasonRaw.join("\n") : String(reasonRaw ?? "");

  return {
    symbol: String(p.symbol ?? params.symbolHint ?? params.existing?.symbol ?? ""),
    phase_number: Number.isFinite(phase) ? phase : 1,
    reason,
    timeframe: (p.timeframe ?? params.existing?.timeframe) ?? null,
    confirmation_criteria: (p.confirmation_criteria ?? p.confirmationNeeded ?? params.existing?.confirmation_criteria) ?? null,
    duration: (p.duration ?? params.existing?.duration) ?? null,
    waiting_note: (p.label ?? params.existing?.waiting_note) ?? null,
    confidence: (p.confidence ?? params.existing?.confidence) ?? null,
    breakout_direction: (p.breakout_direction ?? params.existing?.breakout_direction) ?? null,
    key_level: (p.key_level ?? params.existing?.key_level) ?? null,
    time_remaining_seconds:
      (p.time_remaining_seconds ?? p.timeRemainingSeconds ?? params.existing?.time_remaining_seconds) ?? null,
    market_health_summary: hasFreshSummary
      ? (p.market_health_summary ?? p.marketHealthSummary)
      : phaseChanged
        ? null
        : (params.existing?.market_health_summary ?? null),
    estimated_end_time: (p.estimated_end_time ?? params.existing?.estimated_end_time) ?? null,
    market_conditions: (p.market_conditions ?? params.existing?.market_conditions) ?? null,
  };
}
